/** @jsxImportSource @emotion/react */
import React from 'react';
import {ResortMappingObject, RTPMappingProduct, SiriuswareMappingProduct} from './productMappings';
import { IAppState } from '../core/redux/IAppState';
import { useSelector } from 'react-redux';
import { getResource } from '../language/resources/getResource';
import { css } from '@emotion/react';
import { BLUE_CREEK } from '../styles/colors';
import Table from '../layout/table/Table';
import { ColumnConfig } from '../layout/table/columnConfig';
import { sortByProp } from '../language/module';
import { tableStyle } from './DynamicTable';

interface Props {
  resortMappingObject: ResortMappingObject;
}

const highlightedCell = css `
  border: 5px solid ${BLUE_CREEK} !important;
`;

export const ProductMappingTableUpload = (props: Props) => {

  const isRtp = props.resortMappingObject.posProvider === 'RTP';
  const hasFulfillmentSeason = !!props.resortMappingObject.productMappings[0].fulfillmentSeason;

  const rtpMappingEntitlements = props.resortMappingObject.productMappings as unknown as RTPMappingProduct[];
  const swMappingEntitlements = props.resortMappingObject.productMappings as unknown as SiriuswareMappingProduct[];

  const resources = useSelector((state: IAppState) => state.locale);

  const rtpColumnConfig = (resources: Record<string, any>, hasFulfillmentSeason: boolean) : Array<ColumnConfig<RTPMappingProduct>> => 
    [
      { id: 1, tooltipText: getResource(resources, 'RESORT_MAPPING_COLUMN_PRODUCT'), title: getResource(resources, 'RESORT_MAPPING_COLUMN_PRODUCT'), onSort: sortByProp('product', true) },
      { id: 2, tooltipText: getResource(resources, 'RESORT_MAPPING_COLUMN_SEASON'), title: getResource(resources, 'RESORT_MAPPING_COLUMN_SEASON'), onSort: sortByProp('season', true) },
      { id: 3, tooltipText: getResource(resources, 'RESORT_MAPPING_COLUMN_AGE'), title: getResource(resources, 'RESORT_MAPPING_COLUMN_AGE'), onSort: sortByProp('ageBin', true) },
      ...hasFulfillmentSeason 
      ? [{ id: 4, tooltipText: getResource(resources, 'RESORT_MAPPING_COLUMN_FULFILLMENT_SEASON'), title: getResource(resources, 'RESORT_MAPPING_COLUMN_FULFILLMENT_SEASON'), onSort: sortByProp('fulfillmentSeason', true)}]
      : [],
      { id: 5, tooltipText: 'PHC', title: 'PHC', onSort: sortByProp('productHeaderCode', true) },
      { id: 6, tooltipText: getResource(resources, 'RESORT_MAPPING_COLUMN_ACCESS_RULE'), title: getResource(resources, 'RESORT_MAPPING_COLUMN_ACCESS_RULE'), onSort: sortByProp('accessRule', true) },
      { id: 7, tooltipText: 'MPHC', title: 'MPHC', onSort: sortByProp('mediaProductHeaderCode', true), defaultSortingColumn: true, defaultSortingDirection: 'asc' },
    ]
  

  const swColumnConfig = (resources: Record<string, any>, hasFulfillmentSeason: boolean) : Array<ColumnConfig<SiriuswareMappingProduct>> => 
   [
      { id: 1, tooltipText: getResource(resources, 'RESORT_MAPPING_COLUMN_PRODUCT'), title: getResource(resources, 'RESORT_MAPPING_COLUMN_PRODUCT'), onSort: sortByProp('product', true) },
      { id: 2, tooltipText: getResource(resources, 'RESORT_MAPPING_COLUMN_SEASON'), title: getResource(resources, 'RESORT_MAPPING_COLUMN_SEASON'),  onSort: sortByProp('season', true) },
      { id: 3, tooltipText: getResource(resources, 'RESORT_MAPPING_COLUMN_AGE'), title: getResource(resources, 'RESORT_MAPPING_COLUMN_AGE'), onSort: sortByProp('ageBin', true) },
      ...hasFulfillmentSeason 
      ? [{ id: 4, tooltipText: getResource(resources, 'RESORT_MAPPING_COLUMN_FULFILLMENT_SEASON'), title: getResource(resources, 'RESORT_MAPPING_COLUMN_FULFILLMENT_SEASON'), onSort: sortByProp('fulfillmentSeason', true) }]
      : [],
      { id: 5, tooltipText: getResource(resources, 'RESORT_MAPPING_COLUMN_DEPARTMENT'), title: getResource(resources, 'RESORT_MAPPING_COLUMN_DEPARTMENT'), onSort: sortByProp('department', true) },
      { id: 6, tooltipText: getResource(resources, 'RESORT_MAPPING_COLUMN_CATEGORY'), title: getResource(resources, 'RESORT_MAPPING_COLUMN_CATEGORY'), onSort: sortByProp('category', true) },
      { id: 7, tooltipText: getResource(resources, 'RESORT_MAPPING_COLUMN_ITEM'), title: getResource(resources, 'RESORT_MAPPING_COLUMN_ITEM'), onSort: sortByProp('item', true) },
   ]

  const renderRtpRow = (entitlement: RTPMappingProduct): React.ReactNode => (
    <tr key={entitlement.id}>
      <td>{entitlement.product}</td>
      <td>{entitlement.season}</td>
      <td>{entitlement.ageBin}</td>
      { // Fulfillment Season is not always rendered
          entitlement.fulfillmentSeason
          && <td>{entitlement.fulfillmentSeason}</td>
      }
      <td css={entitlement.productHeaderCode?.includes("->") ? highlightedCell : ""}>{entitlement.productHeaderCode!.trim()}</td>
      <td css={entitlement.accessRule?.includes("->") ? highlightedCell : ""}>{entitlement.accessRule!.trim()}</td>
      <td css={entitlement.mediaProductHeaderCode?.includes("->") ? highlightedCell : ""}>{entitlement.mediaProductHeaderCode!.trim()}</td>
    </tr>
  );

  const renderSwRow = (entitlement: SiriuswareMappingProduct): React.ReactNode => (
    <tr key={entitlement.id}>
      <td>{entitlement.product}</td>
      <td>{entitlement.season}</td>
      <td>{entitlement.ageBin}</td>
      <td>{entitlement.fulfillmentSeason}</td>
      <td css={entitlement.department?.includes("->") ? highlightedCell : ""}>{entitlement.department.trim()}</td>
      <td css={entitlement.category?.includes("->") ? highlightedCell : ""}>{entitlement.category.trim()}</td>
      <td css={entitlement.item?.includes("->") ? highlightedCell : ""}>{entitlement.item.trim()}</td>
    </tr>
  )

  return (
    <div>
      {
       isRtp ?
        <Table
          key="rtpMappingUpload"
          rootCss={css`margin-top: 10px; ${tableStyle}`}
          noDataMessage=''
          isLoading={false}
          columns={rtpColumnConfig(resources, hasFulfillmentSeason)}
          data={rtpMappingEntitlements}
          renderRow={renderRtpRow}
        />
        :  <Table
              key="swMappingUpload"
              rootCss={css`${tableStyle}`}
              noDataMessage=''
              isLoading={false}
              columns={swColumnConfig(resources, hasFulfillmentSeason)}
              data={swMappingEntitlements}
              renderRow={renderSwRow}
            />
      }
    </div>
  )
}
